import { type DependencyList, type RefObject, useEffect, useMemo, useRef } from 'react';

import { noop } from '@/packages/utils/noop';

type SubscribeState = {
  setSubscribeAvailable(): void;
  setSubscribeUnavailable(): void;
  subscribeAvailable: boolean;
};

type LifecycleHookEvent = {
  observer: IntersectionObserver;
  subscribeState: SubscribeState;
  entries: IntersectionObserverEntry[];
};

type UseIntersectionObserverParams<Element extends HTMLElement> = {
  initElement?: Element;
  initSubscribeAvailable?: boolean;
  observerOptions?: IntersectionObserverInit;
  onChange?(params: LifecycleHookEvent): void;
  onInViewport?(params: LifecycleHookEvent): void;
  onOutViewport?(params: LifecycleHookEvent): void;
};

type UseIntersectionObserverResult<Element extends HTMLElement> = {
  elementRef: RefObject<Element>;
  subscribeState: SubscribeState;
};

export function useIntersectionObserver<Element extends HTMLElement = HTMLElement>(
  params: UseIntersectionObserverParams<Element>,
  dependencies: DependencyList,
): UseIntersectionObserverResult<Element> {
  const {
    initElement = null,
    initSubscribeAvailable = true,
    observerOptions,
    onChange = noop,
    onInViewport = noop,
    onOutViewport = noop,
  } = params;

  const subscribeAvailableRef = useRef<boolean>(initSubscribeAvailable);
  const elementRef = useRef<Element>(initElement);

  const subscribeState = useMemo<SubscribeState>(
    () => ({
      setSubscribeAvailable(): void {
        subscribeAvailableRef.current = true;
      },
      setSubscribeUnavailable(): void {
        subscribeAvailableRef.current = false;
      },
      get subscribeAvailable(): boolean {
        return subscribeAvailableRef.current;
      },
    }),
    [],
  );

  useEffect((): void | VoidFunction => {
    if (subscribeAvailableRef.current && elementRef.current) {
      const observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          onInViewport({ entries, observer, subscribeState });
        } else {
          onOutViewport({ entries, observer, subscribeState });
        }

        onChange({ entries, observer, subscribeState });
      }, observerOptions);

      observer.observe(elementRef.current);

      return () => {
        observer.disconnect();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return { elementRef, subscribeState };
}
